import { useState, useEffect, useMemo } from 'react';

import { useHrMatchCount, useResolveIndustry } from '../models/prospectQueries/queries';
import { buildSearchParameters } from '../utils/buildSearchParameters';
import { hasPostalInput } from '../utils/postalCriterion';

const DEBOUNCE_MS = 450;

/**
 * Stable JSON key for a value, so the debounce effect only re-runs when the
 * relevant filter content actually changes (not on identity churn).
 * @param {*} value
 * @returns {string}
 */
const stableKey = (value) => JSON.stringify(value);

const splitTerms = (value) =>
	(value || '')
		.split(',')
		.map((x) => x.trim())
		.filter(Boolean);

/**
 * Whether the HR preview has anything countable beyond the base set.
 * Free-text industry terms count — they are resolved to WZ codes for the preview.
 * @param {object} filters - UI filter state (DEFAULT_FILTERS shape)
 * @returns {boolean}
 */
export const hasHrPreviewCriteria = (filters) =>
	(Array.isArray(filters.industryCodes) && filters.industryCodes.length > 0) ||
	splitTerms(filters.industrySearchTerms).length > 0 ||
	Boolean(filters.locationMode === 'place' && filters.location) ||
	Boolean(filters.locationMode === 'plz' && hasPostalInput(filters)) ||
	Boolean(filters.locationMode === 'state' && (filters.states || []).length > 0) ||
	(filters.legalForm || []).length > 0 ||
	Boolean(
		filters.revenueFrom ||
		filters.revenueTo ||
		filters.balanceTotalFrom ||
		filters.balanceTotalTo ||
		filters.earningsFrom ||
		filters.earningsTo ||
		filters.employeesFrom ||
		filters.employeesTo ||
		filters.eventType,
	);

/**
 * Drives the live, free Handelsregister match-count preview ("Treffer-Trichter")
 * shown in the search popup.
 *
 * Builds the backend HR search parameters from the current UI filter state,
 * debounces them, and fetches the count — but only for HR searches in Germany
 * that have at least one countable criterion. Free-text industry terms are first
 * resolved to explicit WZ codes via /resolveIndustry (once per term set, cached);
 * the count is held back until resolution lands so it never ignores the branch.
 *
 * @param {object} params
 * @param {object} params.filters - UI filter state (DEFAULT_FILTERS shape)
 * @param {'maps'|'hr'} params.searchType - Resolved search type
 * @param {boolean} params.isGermany - Whether the country filter is Germany
 * @param {boolean} params.enabled - Whether the popup is open (gates fetching)
 * @returns {{ data: object|undefined, isLoading: boolean, isResolvingIndustry: boolean, hasCriteria: boolean }}
 */
export const useSearchPreview = ({ filters, searchType, isGermany, enabled }) => {
	const isHR = searchType === 'hr';

	const hasCriteria = hasHrPreviewCriteria(filters);
	const shouldCount = Boolean(enabled && isHR && isGermany && hasCriteria);

	// Build the backend params (reuses the same builder as the real search).
	const searchParameters = useMemo(() => {
		if (!shouldCount) return null;
		return buildSearchParameters(filters, 'hr', splitTerms(filters.industrySearchTerms));
	}, [shouldCount, filters]);

	// Debounce the params so live typing doesn't hit Typesense on every keystroke.
	const [debouncedParams, setDebouncedParams] = useState(null);
	const paramsKey = searchParameters ? stableKey(searchParameters) : null;

	useEffect(() => {
		if (!searchParameters) {
			setDebouncedParams(null);
			return undefined;
		}
		const t = setTimeout(() => setDebouncedParams(searchParameters), DEBOUNCE_MS);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paramsKey]);

	// Free-text terms need one resolver roundtrip (RAG/LLM, ~1-3s) before they can
	// narrow the count; explicit picker codes skip this entirely.
	const termsToResolve = debouncedParams?.industryCodes?.length ? null : debouncedParams?.industrySearchTerms || null;
	const { data: resolvedIndustry, isFetching: isResolvingIndustry } = useResolveIndustry(
		termsToResolve,
		Boolean(shouldCount && termsToResolve),
	);

	// Hold the count until resolution lands so it never counts without the branch
	// filter; a resolution with zero codes falls back to counting without it.
	const countParams = useMemo(() => {
		if (!debouncedParams) return null;
		if (!termsToResolve) return debouncedParams;
		if (!resolvedIndustry) return null;
		const merged = { ...debouncedParams };
		if (resolvedIndustry.expandedCodes?.length > 0) merged.industryCodes = resolvedIndustry.expandedCodes;
		if (resolvedIndustry.codePrefixes?.length > 0) merged.industryCodePrefixes = resolvedIndustry.codePrefixes;
		return merged;
	}, [debouncedParams, termsToResolve, resolvedIndustry]);

	const { data, isFetching } = useHrMatchCount(countParams, shouldCount);

	// "Loading" = a count is wanted but the current filters haven't produced a
	// fresh result yet: debounce pending, resolver pending, or request in flight.
	const debouncedKey = debouncedParams ? stableKey(debouncedParams) : null;
	const isLoading =
		shouldCount && (countParams === null || paramsKey !== debouncedKey || isFetching || isResolvingIndustry);

	return { data, isLoading, isResolvingIndustry, hasCriteria };
};
